import { Location } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { RequestService } from '@df/api/request/request.service';
import { CookieStorageService } from '@df/browser/storage/cookie-storage.service';
import { GlobalStorageService } from '@df/browser/storage/global-storage.service';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { IOnPrerenderModalData } from '@df/browser/storage/prerender/prerender-interfaces';
import { SessionStorageService } from '@df/browser/storage/session-storage.service';
import { App } from '@df/core/app';
import { Client, ISizeName } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { AbstractDfModalInitiatorService } from '@df/ui/modal/abstract-df-modal-initiator.service';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalService } from '@df/ui/modal/modal.service';
import each from 'lodash-es/each';
import groupBy from 'lodash-es/groupBy';
import last from 'lodash-es/last';
import sortBy from 'lodash-es/sortBy';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { blockStatus } from './block-status';

@Injectable({
    providedIn: 'root'
})
export class DfModalService {
    /**
     * Indexed list of registered modal windows
     */
    private static registeredModals: { [id: string]: NDfModal.IBaseModalConfig } = {};

    /**
     * Last modal UID
     */
    private lastModalUId = 0;

    private openModals: { modalRef: ModalRef<NDfModal.IModalComponent>; config: NDfModal.IConfig }[] = [];

    private currentSize?: ISizeName;

    private blockedModals?: string[];

    private subscriptions = new Subscription();

    /**
     * Class constructor
     */
    constructor(
        private modalService: ModalService,
        events: Events,
        private requestService: RequestService,
        injector: Injector,
        private timeoutService: TimeoutService,
        protected client: Client,
        private app: App,
        private sessionStorage: SessionStorageService,
        private localStorage: LocalStorageService,
        private globalStorage: GlobalStorageService,
        private cookieStorageService: CookieStorageService,
        private location: Location,
        private dfApiConfigService: DfApiConfigService
    ) {
        this.subscriptions.add(
            events.on(NDfModal.EventModalOpen).subscribe((options: NDfModal.IOpenRequest | string | string[]) => {
                options = typeof options === 'string' ? [options] : options;
                const id = Array.isArray(options) ? options[0] : options.id;
                const variant = Array.isArray(options) ? options[1] : options.variant;
                const status = !Array.isArray(options) ? options.status : undefined;

                if (id) {
                    this.modalService.closeAll();
                    this.open(status, id, variant);
                }
            })
        );

        // when changing root element many things change and we should destroy any modals (typically cms transition)
        this.subscriptions.add(
            this.client.rootElement$.subscribe(target => {
                this.modalService.closeAll();
            })
        );

        this.blockedModals = cookieStorageService
            .get('modalOff')
            ?.split(',')
            .map(i => i.trim());

        // we need to run in the next js cycle so that dfModalService is already initiated as it will be a dependencey
        // of services instantiated below
        if (this.app.isPlatformBrowser) {
            this.timeoutService.setTimeout(() => {
                dfApiConfigService.loadApiConfig().then(() => {
                    // process each sequence separately
                    each(
                        groupBy(
                            this.getAll().filter(i => !this.isDisabled(i)),
                            i => i.blockGroup || ''
                        ),
                        items => {
                            const sequence = sortBy(items, i => -(i.priority || 0));
                            // preload
                            sequence.forEach(modal => {
                                if ((modal as NDfModal.IModalConfig).prefetch) {
                                    this.loadDependencies(modal);
                                }
                            });

                            // process sequence now
                            const next = () => {
                                const modal = sequence.shift();
                                if (modal) {
                                    const promises = (modal.initiators || [])
                                        .map(initiatorType =>
                                            (injector.get(initiatorType) as AbstractDfModalInitiatorService).initiate(modal)
                                        )
                                        .filter(i => !!i);
                                    // only wait for promiess in conflicting groups
                                    // ignore conflicets in prerender mode (except ssr)
                                    if (
                                        !['prerender', 'legacyPrerender'].includes(this.client.mode) &&
                                        modal.blockGroup &&
                                        promises.length
                                    ) {
                                        Promise.all(promises).finally(next);
                                    } else {
                                        next();
                                    }
                                }
                            };

                            next();
                        }
                    );
                });
            });
        }

        this.subscriptions.add(this.client.obs.breakpoint.subscribe(() => this.onBreakpointChange()));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Component registration
     */
    static register(modal: NDfModal.IConfig): void {
        this.registeredModals[modal.id] = modal;
    }

    loadDependencies(modal: NDfModal.IConfig, variant?: string, options: NDfModal.IOpenOptions = {}): Promise<any> {
        return new Promise<void>(r => r());
    }

    /**
     * Open CMS modal dinwo method
     */
    open(
        status: Status | undefined,
        id: NDfModal.IConfig | string,
        variant?: string,
        data: any = {},
        cb?: (modalRef: ModalRef<any>) => void
    ): Promise<void> {
        const promise = new Promise<void>((resolve, reject) => {
            const modal = typeof id === 'string' ? DfModalService.registeredModals[id] : id;
            if (!modal) {
                resolve();
                return;
            }

            if (
                !this.validateSizeRestriction(modal) ||
                !this.validateLocation(modal) ||
                (this.isDisabled(modal) && !this.forceInCms(modal)) ||
                this.blockedModals?.includes(modal.id)
            ) {
                reject();
                return;
            }
            // protection agains opening after another window requests it
            const uid = ++this.lastModalUId;
            // const hook = {}
            this.requestService.send(
                status,
                () => this.loadDependencies(modal, variant, { createOnError: modal.autoCreate }),
                () => {
                    this.timeoutService.async(() => {
                        const blockId = ['modal', uid];
                        const finish = (modalRef: ModalRef<NDfModal.IModalComponent>) => {
                            if (cb) {
                                cb(modalRef);
                            }
                            resolve();
                        };

                        const modalId = modal.id;

                        const open = () => {
                            blockStatus.groupShow(
                                blockId,
                                ['prerender', 'legacyPrerender'].includes(this.client.mode) ? undefined : modal.blockGroup, // no blocking in prerender
                                false,
                                () => {
                                    // if (this.lastModalUId === uid) {
                                    const config = this.getModalConfig(modal, data);

                                    this.currentSize = this.client.size;

                                    this.timeoutService.async(() => {
                                        const fullConfig = {
                                            ...config,
                                            data,
                                            onClose: () => {
                                                if (config?.onClose) {
                                                    config.onClose();
                                                }

                                                const index = this.openModals.findIndex(i => i.modalRef === modalRef);
                                                if (index > -1) {
                                                    this.openModals.splice(index, 1);
                                                }

                                                blockStatus.groupCloseNotify(blockId);
                                            }
                                        };

                                        // if data will contain information about item being visually hidden then we adjust options
                                        if (data && typeof data === 'object' && (data as IOnPrerenderModalData).visuallyHidden) {
                                            const panelClass: string[] = fullConfig.panelClass
                                                ? Array.isArray(fullConfig.panelClass)
                                                    ? fullConfig.panelClass
                                                    : [fullConfig.panelClass]
                                                : [];
                                            fullConfig.panelClass = [...panelClass, 'visually-hidden'];
                                            fullConfig.hasBackdrop = false;
                                            fullConfig.scrollStrategy = 'noop';
                                        }

                                        const modalRef = this.modalService.open(
                                            modal.component,
                                            fullConfig,
                                            typeof modal.position === 'function' ? modal.position(this.client.size, data) : modal.position,
                                            ref => this.onCreateComponent(ref.componentInstance, modal, variant)
                                        );

                                        this.openModals.push({ modalRef: modalRef, config: modal });

                                        finish(modalRef);
                                    });
                                    return true;
                                },
                                () => {
                                    this.app.logger.warn('Missing modal block data', modal);
                                }
                            );
                        };

                        const existing = this.openModals.filter(i => i.config.id === modalId);
                        if (existing.length) {
                            switch (modal.reopenPolicy) {
                                case NDfModal.ReopenPolicy.wait:
                                    last(existing)
                                        ?.modalRef.afterClosed()
                                        .pipe(take(1))
                                        .subscribe(() => open());
                                    break;
                                case NDfModal.ReopenPolicy.first:
                                    finish(existing[0].modalRef);
                                    break;

                                case NDfModal.ReopenPolicy.passData:
                                    existing.forEach(i => {
                                        if (i.modalRef.componentInstance.updateModalData) {
                                            i.modalRef.componentInstance.updateModalData(data);
                                        }
                                    });
                                    finish(existing[0].modalRef);
                                    break;
                                case NDfModal.ReopenPolicy.last:
                                    existing.forEach(i => i.modalRef.close());
                                    open();
                                    break;
                                default:
                                    open();
                                    break;
                            }
                        } else {
                            open();
                        }
                    });
                },
                reject
            );
        });
        // no need to warn
        promise.catch(() => {});
        return promise;
    }

    protected onCreateComponent(componentInstance: any, modal: NDfModal.IConfig, variant?: string) {}

    private getModalConfig(modal: NDfModal.IBaseModalConfig, data?: any) {
        return typeof modal.config === 'function' ? modal.config(this.client.size, data) : modal.config;
    }

    getAll(): NDfModal.IConfig[] {
        return Object.values(DfModalService.registeredModals);
    }

    /////////////////////////////////////
    //
    // disabling
    //
    /////////////////////////////////////

    isDisabled(modal: NDfModal.IConfig): boolean {
        // no modals in cms
        if (this.client.isCms && modal.allowInCms === false) {
            return true;
        }
        // if prerender then no modal unless rquested so
        if (this.client.isPrerender && !modal.prerenderAllowed) {
            return true;
        }
        const id = this.toStorageId(modal);
        return !!(this.globalStorage.get(id) || this.localStorage.get(id) || this.sessionStorage.get(id));
    }

    disableGlobal(modal: NDfModal.IModalConfig): void {
        this.globalStorage.set(this.toStorageId(modal), 1);
    }

    disableSession(modal: NDfModal.IModalConfig): void {
        this.sessionStorage.set(this.toStorageId(modal), 1);
    }

    disableWebsite(modal: NDfModal.IModalConfig): void {
        this.localStorage.set(this.toStorageId(modal), 1);
    }

    private toStorageId(modal: NDfModal.IConfig): string {
        return `modalInitiator.${modal.id}.off`;
    }

    private onBreakpointChange() {
        each(this.openModals, modal => {
            // TODO: fix logic against current size, it is not correct
            if (!this.validateSizeRestriction(modal.config, this.currentSize)) {
                modal.modalRef.close();
            }
        });
    }

    /**
     * Checks modal against size restrirtions
     * returns true if can be used
     */
    private validateSizeRestriction(config: NDfModal.IConfig, vsSize = this.client.size): boolean {
        const sizeRestriction = config.sizeRestriction;

        if (!sizeRestriction) {
            return true;
        }

        const size = this.client.size;
        const sizes = typeof sizeRestriction === 'function' ? sizeRestriction(vsSize) : sizeRestriction;
        return sizes.includes(size);
    }

    /**
     * Checks if modal can be shown on given location
     */
    private validateLocation(config: NDfModal.IConfig): boolean {
        const allowedLocation = config.allowedLocation;
        const forbiddenLocation = config.forbiddenLocation;

        if (allowedLocation) {
            if (forbiddenLocation && allowedLocation.test(this.location.path())) {
                return true;
            } else if (!forbiddenLocation) {
                return allowedLocation.test(this.location.path());
            }
        }

        if (forbiddenLocation) {
            return !forbiddenLocation.test(this.location.path());
        }

        return true;
    }

    private forceInCms(modal: NDfModal.IConfig): boolean {
        if (this.client.isCms && modal.allowInCms === true) {
            return true;
        }
        return false;
    }
}
